// 🔧 event_settings.js
// ✅ Purpose: Manager-facing event-level settings page
// ✅ Features:
// - Edits event name and slug
// - Warns that slug changes can break old URLs and SEO
// - Checks slug availability against existing events
// - Offers an available "-1" slug suggestion when duplicate slug is found
// - Edits event tagline shown under the public event title
// - Edits event location and event street address for future Google Maps use
// - Edits event website
// - Edits start and end dates
// - Edits attendance estimate and how-heard source captured during event request
// - Controls Private Event visibility
// - Private events are hidden from LGV event search and search engines
// - Direct event links still work for private events
// - Saves settings directly to /events/{eventId}
// - Shows success/error notifications for 20 seconds
// - Fully block-commented for clarity and future devs

import React, { useEffect, useState } from "react";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { Link } from "react-router-dom";
import { db } from "../../firebase";
import { useEventContext } from "../../hooks/use_event_context";
import FullPageSpinner from "../../components/FullPageSpinner";
import EventVisibilityControl from "../../components/event_visibility_control";
import { isEventPublic, getEventVisibilityFields } from "../../utils/event_visibility";
import { logger } from "../../utils/logger";
import { showStatus } from "../../utils/status_helpers";
import {
  ATTENDANCE_ESTIMATES,
  HOW_HEARD_OPTIONS,
} from "../../constants/request_intake_options";

const SOCIAL_PLATFORM_OPTIONS = [
  { key: "facebook", label: "Facebook", placeholder: "https://facebook.com/YourPage" },
  { key: "instagram", label: "Instagram", placeholder: "https://instagram.com/youraccount" },
  { key: "linkedin", label: "LinkedIn", placeholder: "https://linkedin.com/company/yourcompany" },
  { key: "youtube", label: "YouTube", placeholder: "https://youtube.com/@yourchannel" },
  { key: "tiktok", label: "TikTok", placeholder: "https://tiktok.com/@youraccount" },
  { key: "threads", label: "Threads", placeholder: "https://threads.net/@youraccount" },
  { key: "x", label: "X / Twitter", placeholder: "https://x.com/youraccount" },
  { key: "other", label: "Other", placeholder: "https://example.com/social" },
];

const EventSettings = () => {
  const {
    eventslug,
    eventId,
    eventDoc,
    eventLoading,
    ready,
  } = useEventContext({
    redirectIfNotFound: true,
  });

  const [form, setForm] = useState({
    name: "",
    slug: "",
    eventTagline: "",
    eventLocation: "",
    eventAddress: "",
    eventWebsite: "",
    socialLinks: [],
    startDate: "",
    endDate: "",
    attendanceEstimate: "not_sure",
    howHeardAboutUs: "",
    publicSeo: false,
  });

  const [originalSlug, setOriginalSlug] = useState("");
  const [slugAvailable, setSlugAvailable] = useState(true);
  const [checkingSlug, setCheckingSlug] = useState(false);
  const [suggestedSlug, setSuggestedSlug] = useState("");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [status, setStatus] = useState(null);

  useEffect(() => {
    if (!status?.message) return;

    const timer = setTimeout(() => {
      setStatus(null);
    }, 20000);

    return () => clearTimeout(timer);
  }, [status]);

  const formatDateForInput = (value) => {
    if (!value) return "";

    const date = value?.toDate ? value.toDate() : new Date(value);

    if (Number.isNaN(date.getTime())) return "";

    return date.toISOString().split("T")[0];
  };

  const slugify = (value) => {
    return String(value || "")
      .toLowerCase()
      .trim()
      .replace(/\s+/g, "-")
      .replace(/[^\w-]/g, "")
      .replace(/-+/g, "-")
      .replace(/^-+|-+$/g, "");
  };

  const checkSlugAvailability = async (slugValue) => {
    const cleanSlug = slugify(slugValue);

    if (!cleanSlug) {
      setSlugAvailable(false);
      setSuggestedSlug("");
      return;
    }

    if (cleanSlug === originalSlug) {
      setSlugAvailable(true);
      setSuggestedSlug("");
      return;
    }

    try {
      setCheckingSlug(true);

      const slugQuery = query(
        collection(db, "events"),
        where("slug", "==", cleanSlug),
        limit(1)
      );

      const snap = await getDocs(slugQuery);

      if (snap.empty || snap.docs[0].id === eventId) {
        setSlugAvailable(true);
        setSuggestedSlug("");
        return;
      }

      setSlugAvailable(false);

      const fallbackSlug = `${cleanSlug}-1`;

      const fallbackQuery = query(
        collection(db, "events"),
        where("slug", "==", fallbackSlug),
        limit(1)
      );

      const fallbackSnap = await getDocs(fallbackQuery);

      setSuggestedSlug(fallbackSnap.empty ? fallbackSlug : "");
    } catch (err) {
      logger.error("❌ Failed checking event slug:", {
        value: err,
      });

      setSlugAvailable(false);
      setSuggestedSlug("");
    } finally {
      setCheckingSlug(false);
    }
  };

  useEffect(() => {
    const loadEventSettings = async () => {
      if (!eventId) return;

      setLoading(true);

      try {
        const eventSnap = await getDoc(doc(db, "events", eventId));
        const data = eventSnap.exists() ? eventSnap.data() : eventDoc || {};

        const loadedSlug = data.slug || eventslug || "";

        setOriginalSlug(loadedSlug);

        setForm({
          name: data.name || "",
          slug: loadedSlug,
          eventTagline: data.eventTagline || "",
          eventLocation: data.eventLocation || "",
          eventAddress: data.eventAddress || "",
          eventWebsite: data.eventWebsite || data.marketingUrl || "",

            socialLinks: Array.isArray(data.socialLinks)
            ? data.socialLinks
            : [
                data.facebookUrl
                    ? { platform: "facebook", label: "Facebook", url: data.facebookUrl }
                    : null,
                data.instagramUrl
                    ? { platform: "instagram", label: "Instagram", url: data.instagramUrl }
                    : null,
                data.linkedinUrl
                    ? { platform: "linkedin", label: "LinkedIn", url: data.linkedinUrl }
                    : null,
                ].filter(Boolean),

          startDate: formatDateForInput(data.startDate),
          endDate: formatDateForInput(data.endDate),
          attendanceEstimate: data.attendanceEstimate || "not_sure",
          howHeardAboutUs: data.howHeardAboutUs || "",
          publicSeo: isEventPublic(data),
        });

        logger.log("✅ Event settings loaded", {
          eventId,
        });
      } catch (err) {
        logger.error("❌ Failed to load event settings:", {
          value: err,
        });

        showStatus(setStatus, {
          type: "error",
          message: "Unable to load event settings.",
        });
      } finally {
        setLoading(false);
      }
    };

    if (ready && eventId) {
      loadEventSettings();
    }
  }, [ready, eventId, eventDoc, eventslug]);

    const handleAddSocialLink = () => {
    setForm((prev) => ({
        ...prev,
        socialLinks: [
        ...(prev.socialLinks || []),
        {
            platform: "facebook",
            label: "Facebook",
            url: "",
        },
        ],
    }));
    };

    const handleSocialLinkChange = (index, field, value) => {
    setForm((prev) => {
        const nextLinks = [...(prev.socialLinks || [])];

        nextLinks[index] = {
        ...nextLinks[index],
        [field]: value,
        };

        if (field === "platform") {
        const selected = SOCIAL_PLATFORM_OPTIONS.find(
            (option) => option.key === value
        );

        nextLinks[index].label = selected?.label || "";
        }

        return {
        ...prev,
        socialLinks: nextLinks,
        };
    });
    };

    const handleRemoveSocialLink = (index) => {
    setForm((prev) => ({
        ...prev,
        socialLinks: (prev.socialLinks || []).filter((_, linkIndex) => linkIndex !== index),
    }));
    };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    let nextValue = type === "checkbox" ? checked : value;

    if (name === "slug") {
      nextValue = slugify(value);
      checkSlugAvailability(nextValue);
    }

    setForm((prev) => ({
      ...prev,
      [name]: nextValue,
    }));
  };

  const useSuggestedSlug = () => {
    if (!suggestedSlug) return;

    setForm((prev) => ({
      ...prev,
      slug: suggestedSlug,
    }));

    setSlugAvailable(true);
    setSuggestedSlug("");
  };

  const handleSave = async (e) => {
    e.preventDefault();

    if (!form.name.trim()) {
      showStatus(setStatus, {
        type: "error",
        message: "Event name is required.",
      });
      return;
    }

    if (!form.slug.trim()) {
      showStatus(setStatus, {
        type: "error",
        message: "Event slug is required.",
      });
      return;
    }

    if (!slugAvailable) {
      showStatus(setStatus, {
        type: "error",
        message: "Please choose an available event slug before saving.",
      });
      return;
    }

    const slugChanged = form.slug !== originalSlug;

    if (slugChanged) {
      const confirmed = window.confirm(
        "Changing the Event Slug will change the public event URL.\n\nOld links, QR references, shared links, and SEO history may stop working unless redirects are added later.\n\nAre you sure you want to change the event slug?"
      );

      if (!confirmed) return;
    }

    setSaving(true);

    try {
      await updateDoc(doc(db, "events", eventId), {
        name: String(form.name || "").trim(),
        slug: slugify(form.slug),
        eventTagline: String(form.eventTagline || "").trim(),
        eventLocation: String(form.eventLocation || "").trim(),
        eventAddress: String(form.eventAddress || "").trim(),
        eventWebsite: String(form.eventWebsite || "").trim(),

        socialLinks: (form.socialLinks || [])
        .map((link) => ({
            platform: String(link.platform || "").trim(),
            label: String(link.label || "").trim(),
            url: String(link.url || "").trim(),
        }))
        .filter((link) => link.url),

        startDate: form.startDate ? new Date(form.startDate) : null,
        endDate: form.endDate ? new Date(form.endDate) : null,
        attendanceEstimate: form.attendanceEstimate || "not_sure",
        howHeardAboutUs: form.howHeardAboutUs || "",
        // ✅ Backward compatibility:
        // publicSeo remains available for existing SEO/sitemap logic.
        // Private events should never be indexable.
        ...getEventVisibilityFields(form.publicSeo),

        updatedAt: serverTimestamp(),
      });

      setOriginalSlug(slugify(form.slug));

      logger.log("✅ Event settings saved", {
        eventId,
      });

      showStatus(setStatus, {
        type: "success",
        message: "✅ Event settings saved successfully.",
      });
    } catch (err) {
      logger.error("❌ Failed saving event settings:", {
        value: err,
      });

      showStatus(setStatus, {
        type: "error",
        message: "Unable to save event settings.",
      });
    } finally {
      setSaving(false);
    }
  };

  if (!ready || !eventId || eventLoading || loading) {
    return <FullPageSpinner message="Loading Event Settings..." />;
  }

  return (
    <section id="top-content" className="container-fluid py-5">
      <div className="container" style={{ maxWidth: "1000px" }}>
        <div className="row mb-4">
          <div className="col-12 col-lg-8">
            <h1 className="display-5 mb-2">⚙️ Event Settings</h1>

            <p className="lead text-muted mb-0">
              Manage event details, dates, location, tagline, and privacy.
            </p>
          </div>

          <div className="col-12 col-lg-4 mt-3 mt-lg-0 d-flex justify-content-lg-end align-items-start">
            <Link
              to={`/${eventslug}/event`}
              className="btn btn-outline-primary"
              target="_blank"
              rel="noopener noreferrer"
            >
              🌐 View Event Home
            </Link>
          </div>
        </div>

        {status?.message && (
          <div
            className={`alert ${
              status.type === "success"
                ? "alert-success"
                : status.type === "warning"
                ? "alert-warning"
                : status.type === "error"
                ? "alert-danger"
                : "alert-info"
            }`}
          >
            {status.message}
          </div>
        )}

        <form onSubmit={handleSave}>
          <div className="card shadow-sm border-0 mb-4">
            <div className="card-header bg-white">
              <strong>Core Event Details</strong>
            </div>

            <div className="card-body">
              <div className="row g-3">
                <div className="col-12">
                  <label className="form-label fw-bold">Event Name*</label>

                  <input
                    type="text"
                    name="name"
                    className="form-control"
                    value={form.name}
                    onChange={handleChange}
                    placeholder="Event name"
                  />
                </div>

                <div className="col-12">
                  <label className="form-label fw-bold">Event Slug*</label>

                  <input
                    type="text"
                    name="slug"
                    className={`form-control ${
                      form.slug
                        ? slugAvailable
                          ? "is-valid"
                          : "is-invalid"
                        : ""
                    }`}
                    value={form.slug}
                    onChange={handleChange}
                    placeholder="event-url-slug"
                  />

                  {checkingSlug && (
                    <div className="form-text">
                      Checking slug availability...
                    </div>
                  )}

                  {!checkingSlug && form.slug && slugAvailable && (
                    <div className="valid-feedback">
                      Slug is available.
                    </div>
                  )}

                  {!checkingSlug && form.slug && !slugAvailable && (
                    <div className="invalid-feedback d-block">
                      This slug is already being used by another event.
                    </div>
                  )}

                  {suggestedSlug && (
                    <div className="alert alert-warning mt-2 mb-0">
                      <div className="fw-bold">
                        Suggested available slug:
                      </div>

                      <div className="d-flex flex-column flex-md-row gap-2 align-items-md-center mt-2">
                        <code>{suggestedSlug}</code>

                        <button
                          type="button"
                          className="btn btn-sm btn-warning"
                          onClick={useSuggestedSlug}
                        >
                          Use Suggested Slug
                        </button>
                      </div>
                    </div>
                  )}

                  <div className="form-text">
                    Public URL: https://letsgovote.app/{form.slug || "event-slug"}
                    <br />
                    Changing this can break old links, QR references, shared links, and SEO history.
                  </div>
                </div>

                <div className="col-12">
                  <label className="form-label fw-bold">
                    Event Tagline
                  </label>

                  <input
                    type="text"
                    name="eventTagline"
                    className="form-control"
                    value={form.eventTagline}
                    onChange={handleChange}
                    maxLength={120}
                    placeholder="Example: Oregon's Largest Air-Cooled VW Show"
                  />

                  <div className="form-text">
                    Displayed beneath the event title on the public event page.
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="card shadow-sm border-0 mb-4">
            <div className="card-header bg-white">
              <strong>Location + Website</strong>
            </div>

            <div className="card-body">
              <div className="row g-3">
                <div className="col-12">
                  <label className="form-label fw-bold">
                    Event Location
                  </label>

                  <input
                    type="text"
                    name="eventLocation"
                    className="form-control"
                    value={form.eventLocation}
                    onChange={handleChange}
                    placeholder="Example: Portland International Raceway"
                  />

                  <div className="form-text">
                    Displayed under the event title on the public event page.
                  </div>
                </div>

                <div className="col-12">
                  <label className="form-label fw-bold">
                    Event Address
                  </label>

                  <input
                    type="text"
                    name="eventAddress"
                    className="form-control"
                    value={form.eventAddress}
                    onChange={handleChange}
                    placeholder="Example: 1940 N Victory Blvd, Portland, OR 97217"
                  />

                  <div className="form-text">
                    Use a full street address so LGV can link or map the event location later.
                  </div>
                </div>

                <div className="col-12">
                  <label className="form-label fw-bold">
                    Event Website
                  </label>

                  <input
                    type="url"
                    name="eventWebsite"
                    className="form-control"
                    value={form.eventWebsite}
                    onChange={handleChange}
                    placeholder="https://example.com"
                  />

                  <div className="form-text">
                    Optional external website for this event or organization.
                  </div>
                </div>
              </div>
            </div>
          </div>

            <div className="card shadow-sm border-0 mb-4">
            <div className="card-header bg-white d-flex flex-column flex-md-row justify-content-between gap-2">
                <div>
                <strong>Social Media & Sharing</strong>
                <div className="small text-muted">
                    Add any social profiles or community links associated with this event.
                </div>
                </div>

                <button
                type="button"
                className="btn btn-outline-primary btn-sm align-self-start align-self-md-center"
                onClick={handleAddSocialLink}
                >
                ➕ Add Social Link
                </button>
            </div>

            <div className="card-body">
                {(form.socialLinks || []).length === 0 ? (
                <div className="alert alert-light border mb-0">
                    No social links have been added yet.
                </div>
                ) : (
                <div className="row g-3">
                    {(form.socialLinks || []).map((link, index) => {
                    const selectedPlatform = SOCIAL_PLATFORM_OPTIONS.find(
                        (option) => option.key === link.platform
                    );

                    return (
                        <div className="col-12" key={`social-link-${index}`}>
                        <div className="border rounded p-3 bg-light">
                            <div className="row g-3 align-items-end">
                            <div className="col-12 col-md-3">
                                <label className="form-label fw-bold">
                                Platform
                                </label>

                                <select
                                className="form-select"
                                value={link.platform || "facebook"}
                                onChange={(e) =>
                                    handleSocialLinkChange(index, "platform", e.target.value)
                                }
                                >
                                {SOCIAL_PLATFORM_OPTIONS.map((option) => (
                                    <option key={option.key} value={option.key}>
                                    {option.label}
                                    </option>
                                ))}
                                </select>
                            </div>

                            <div className="col-12 col-md-3">
                                <label className="form-label fw-bold">
                                Display Label
                                </label>

                                <input
                                type="text"
                                className="form-control"
                                value={link.label || ""}
                                onChange={(e) =>
                                    handleSocialLinkChange(index, "label", e.target.value)
                                }
                                placeholder="Facebook"
                                />
                            </div>

                            <div className="col-12 col-md-4">
                                <label className="form-label fw-bold">
                                URL
                                </label>

                                <input
                                type="url"
                                className="form-control"
                                value={link.url || ""}
                                onChange={(e) =>
                                    handleSocialLinkChange(index, "url", e.target.value)
                                }
                                placeholder={selectedPlatform?.placeholder || "https://"}
                                />
                            </div>

                            <div className="col-12 col-md-2">
                                <button
                                type="button"
                                className="btn btn-outline-danger w-100"
                                onClick={() => handleRemoveSocialLink(index)}
                                >
                                Remove
                                </button>
                            </div>
                            </div>
                        </div>
                        </div>
                    );
                    })}
                </div>
                )}

                <div className="form-text mt-3">
                These links can be shown on the public event page so attendees can follow the event or organization.
                </div>
            </div>
            </div>

          <div className="card shadow-sm border-0 mb-4">
            <div className="card-header bg-white">
              <strong>Dates + Intake Details</strong>
            </div>

            <div className="card-body">
              <div className="row g-3">
                <div className="col-12 col-md-6">
                  <label className="form-label fw-bold">
                    Start Date
                  </label>

                  <input
                    type="date"
                    name="startDate"
                    className="form-control"
                    value={form.startDate}
                    onChange={handleChange}
                  />
                </div>

                <div className="col-12 col-md-6">
                  <label className="form-label fw-bold">
                    End Date
                  </label>

                  <input
                    type="date"
                    name="endDate"
                    className="form-control"
                    value={form.endDate}
                    onChange={handleChange}
                  />
                </div>

                <div className="col-12 col-md-6">
                  <label className="form-label fw-bold">
                    Attendance Estimate
                  </label>

                  <select
                    name="attendanceEstimate"
                    className="form-select"
                    value={form.attendanceEstimate}
                    onChange={handleChange}
                  >
                    {ATTENDANCE_ESTIMATES.map((estimate) => (
                      <option key={estimate.value} value={estimate.value}>
                        {estimate.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-12 col-md-6">
                  <label className="form-label fw-bold">
                    How did they hear about us?
                  </label>

                  <select
                    name="howHeardAboutUs"
                    className="form-select"
                    value={form.howHeardAboutUs}
                    onChange={handleChange}
                  >
                    <option value="">Select one</option>

                    {HOW_HEARD_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          <div className="card shadow-sm border-0 mb-4">
            <div className="card-header bg-white">
              <strong>Privacy</strong>
            </div>

            <div className="card-body">
              <EventVisibilityControl
                publicEvent={form.publicSeo}
                onChange={(publicSeo) => setForm((current) => ({ ...current, publicSeo }))}
                disabled={saving}
              />
            </div>
          </div>

          <div className="d-flex flex-wrap gap-2">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={saving || checkingSlug || !slugAvailable}
            >
              {saving ? "Saving..." : "💾 Save Event Settings"}
            </button>

            <Link
              to={`/${eventslug}/event`}
              className="btn btn-outline-secondary"
            >
              Back to Event Home
            </Link>
          </div>
        </form>
      </div>
    </section>
  );
};

export default EventSettings;
